<template>
  <div>
    <v-card elevation="0">
      <v-card-text class="px-8 py-6">
        <h2 class="text-h5 font-weight-bold text-center mb-3">
          {{ displayTitle }}
        </h2>

        <p class="text-center text--secondary mb-8" style="font-size: 15px; line-height: 1.6;">
          {{ displaySubtitle }}
        </p>

        <div class="method-list">
          <v-card
              v-for="method in availableMethods"
              :key="method.slug"
              outlined
              class="method-card mb-3"
              :class="{'method-card--selected': selectedMethod === method.slug}"
              @click="selectMethod(method)"
              hover
          >
            <v-card-text class="d-flex align-center pa-4">
              <v-avatar size="48" color="grey lighten-3" class="mr-4">
                <v-icon size="28" color="grey darken-1">{{ method.icon }}</v-icon>
              </v-avatar>

              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-medium mb-1">
                  {{ $t('2fa.method.' + method.slug + '.name') }}
                </div>
                <div class="text-caption text--secondary">
                  {{ $t('2fa.method.' + method.slug + '_description') }}
                </div>
              </div>

              <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
            </v-card-text>
          </v-card>
        </div>

        <template v-if="backupCodesMethod || showBackButton || showDismissButton">
          <v-divider class="my-5"></v-divider>
          <div class="d-flex justify-center flex-wrap" style="gap: 4px;">
            <v-btn
                v-if="backupCodesMethod"
                text
                small
                @click="selectMethod(backupCodesMethod)"
                class="text-none"
                color="grey darken-1"
            >
              <v-icon small left>mdi-shield-key-outline</v-icon>
              {{ $t('2fa.use_backup_code') }}
            </v-btn>

            <v-btn
                v-if="showBackButton"
                text
                small
                @click="backToLogin"
                class="text-none"
                color="grey darken-1"
            >
              {{ $t('2fa.back_to_login') }}
            </v-btn>

            <v-btn
                v-if="showDismissButton"
                text
                small
                @click="onDismiss"
                class="text-none"
                color="grey darken-1"
            >
              {{ $t('2fa.method.register.remind_later') }}
            </v-btn>
          </div>
        </template>

      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {twoFactorAuthStore} from '@/store/pinia/twoFactorAuthStore'
import AppLogo from '@/components/_app/AppLogo.vue'

export default {
  name: 'TwoFactorMethodSelector',
  components: {AppLogo},
  props: {
    methods: {
      type: Array,
      default: null
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    showDismissButton: {
      type: Boolean,
      default: false
    },
    showBackButton: {
      type: Boolean,
      default: true
    },
    autoSelectSingle: {
      type: Boolean,
      default: true
    }
  },
  setup() {
    const twoFactorAuth = twoFactorAuthStore()
    return {twoFactorAuth}
  },
  data() {
    return {
      selectedMethod: null,
    }
  },

  mounted() {
    if (this.availableMethods.length === 0) {
      this.$emit('methods-empty', null);
    }

    if (this.availableMethods.length === 1 && this.autoSelectSingle) {
      this.twoFactorAuth.selected2FAMethod = this.availableMethods[0];
      this.$emit('method-selected', this.availableMethods[0]);
    }
  },

  methods: {
    backToLogin() {
      this.$emit('back-to-login', null);
    },

    onDismiss(){
      this.$emit('dismiss', null);
    },

    selectMethod(method) {
      this.selectedMethod = method.slug;
      this.twoFactorAuth.selected2FAMethod = method;
      this.$emit('method-selected', method);
    },
  },

  computed: {
    availableMethods() {
      const methods = this.methods || this.twoFactorAuth.available2FAMethods;
      return methods.filter(m => m.slug !== 'backup_codes');
    },
    backupCodesMethod() {
      const methods = this.methods || this.twoFactorAuth.available2FAMethods;
      return methods.find(m => m.slug === 'backup_codes' && m.is_enabled) || null;
    },
    displayTitle() {
      return this.title || this.$t('2fa.method_selection.title');
    },
    displaySubtitle() {
      return this.subtitle || this.$t('2fa.method_selection.subtitle');
    }
  }
}
</script>

<style lang="scss" scoped>
.method-list {
  margin-bottom: 1rem;
}

.method-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;

  &:hover {
    border-color: rgba(0, 0, 0, 0.24) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &--selected {
    border-color: var(--v-primary-base) !important;
    background-color: rgba(var(--v-primary-base), 0.02);
  }
}

::v-deep .v-card__text {
  padding: 16px;
}
</style>
